import React, { useState, useEffect, useCallback } from 'react';
import { ptzApi } from '../../services/api';
import { useAppStore } from '../../store/appStore';

// PTZ control settings, extracted from PTZController into its own tab. Owns the six
// speed sliders — the single source of truth, persisted to the same per-camera
// localStorage keys the live PTZ pad reads at gesture time (so changes apply live with
// no shared React state). Also provides Position readout + Zoom Calibration via its own
// lightweight API calls (decoupled from the pad's self-calibrating zoom slider).
const SPEED_SLIDERS = [
  ['Joystick Speed',     'joy',       '50'],
  ['D-Pad Speed',        'dpad',      '50'],
  ['Zoom Speed',         'zoom',      '50'],
  ['Preset Speed',       'preset',    '50'],
  ['Click-Center Speed', 'center',    '100'],
  ['Mouse Zoom Speed',   'mousezoom', '50'],
];

function SpeedSlider({ cameraId, label, k, def }) {
  const storageKey = `axisvms_ptz_${k}_speed_${cameraId}`;
  const [val, setVal] = useState(() => parseInt(localStorage.getItem(storageKey) || def, 10));
  // Re-seed when switching cameras so the slider reflects that camera's stored value.
  useEffect(() => { setVal(parseInt(localStorage.getItem(storageKey) || def, 10)); }, [storageKey, def]);
  function onChange(e) {
    const v = parseInt(e.target.value, 10);
    setVal(v);
    localStorage.setItem(storageKey, v);
  }
  return (
    <div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '4px' }}>
        {label}: {val}%
      </div>
      <input type="range" min="1" max="100" value={val} onChange={onChange} style={{ width: '100%', accentColor: 'var(--accent-orange)' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
        <span>Slow</span><span>Fast</span>
      </div>
    </div>
  );
}

export default function PTZSettingsPanel({ camera }) {
  const { addToast } = useAppStore();
  const [position, setPosition] = useState({ pan: null, tilt: null, zoom: null });
  const [zoomLimits, setZoomLimits] = useState(null);
  const [calibrating, setCalibrating] = useState(false);

  const loadPosition = useCallback(async () => {
    try {
      const res = await ptzApi.ptzPosition(camera.id);
      setPosition(res.data);
    } catch { /* leave previous readout */ }
  }, [camera.id]);

  useEffect(() => {
    loadPosition();
    ptzApi.ptzLimits(camera.id).then(r => setZoomLimits(r.data)).catch(() => {});
  }, [loadPosition, camera.id]);

  async function runCalibrateZoom() {
    setCalibrating(true);
    try {
      const res = await ptzApi.calibrateZoom(camera.id);
      const { minZoom, maxZoom } = res.data;
      setZoomLimits({ minZoom, maxZoom });
      addToast({ type: 'success', message: `Zoom calibrated: min=${minZoom.toFixed ? minZoom.toFixed(1) : minZoom}, max=${maxZoom.toFixed ? maxZoom.toFixed(1) : maxZoom}` });
      loadPosition();
    } catch (err) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Zoom calibration failed' });
    } finally {
      setCalibrating(false);
    }
  }

  return (
    <div style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div className="section-title">PTZ Settings</div>

      {/* Position */}
      <div>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '6px' }}>Position</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '8px' }}>
          {[['Pan', position.pan], ['Tilt', position.tilt], ['Zoom', position.zoom]].map(([label, val]) => (
            <div key={label} style={{ textAlign: 'center', background: 'var(--bg-void)', borderRadius: 'var(--radius-sm)', padding: '6px 4px' }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '14px', fontWeight: 600, color: 'var(--accent-orange)' }}>
                {typeof val === 'number' ? Math.round(val) : '—'}
              </div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{label}</div>
            </div>
          ))}
        </div>
        <button className="btn btn-ghost btn-sm" style={{ width: '100%', justifyContent: 'center' }} onClick={loadPosition}>
          Refresh Position
        </button>
      </div>

      {/* Zoom Calibration */}
      <div>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '6px' }}>Zoom Calibration</div>
        {zoomLimits && (
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--text-muted)', marginBottom: '6px' }}>
            Range: {typeof zoomLimits.minZoom === 'number' ? zoomLimits.minZoom.toFixed(1) : zoomLimits.minZoom} – {typeof zoomLimits.maxZoom === 'number' ? zoomLimits.maxZoom.toFixed(1) : zoomLimits.maxZoom}
          </div>
        )}
        <button className="btn btn-ghost btn-sm" style={{ width: '100%', justifyContent: 'center' }} onClick={runCalibrateZoom} disabled={calibrating}>
          {calibrating ? 'Calibrating… (camera is moving)' : 'Calibrate Zoom Range'}
        </button>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', color: 'var(--text-muted)', marginTop: '4px', lineHeight: 1.4 }}>
          Camera will zoom fully out then fully in to discover the actual optical range. Takes ~45 sec for Dahua, ~30 sec for Axis.
        </div>
      </div>

      {/* Speed sliders — single source of truth, read live by the PTZ pad from localStorage */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {SPEED_SLIDERS.map(([label, k, def]) => (
          <SpeedSlider key={k} cameraId={camera.id} label={label} k={k} def={def} />
        ))}
      </div>
    </div>
  );
}
